/**
 * 게이트웨이 응답 봉투(`{ status: "Success" | "Failed", data }`)의 실패를 **경계에서 오류로 승격**한다.
 *
 * 이 승격이 계약의 1차 규범인 이유는 결함이 화면이 아니라 계층을 따라 갈리기 때문이다 —
 * 봉투를 경계에서 승격하는 도메인은 화면 코드를 고치지 않아도 실패가 `isError` 로 나타나고,
 * 승격하지 않는 도메인은 화면마다 `if (!data || data.status === "Failed") return []` 를
 * 반복하다 한 곳씩 빠뜨린다. 승격 후에는 전송 실패와 봉투 실패가 하나의 신호로 합쳐지므로
 * 렌더 지점이 실패 모드를 구별할 필요가 없다.
 *
 * 봉투 타입에 구조적으로만 의존한다 — 타입 SoT(`projects-list/type.ts`)를 import 하면
 * `shared → projects-list` 역방향 의존이 생기므로 그렇게 하지 않는다.
 *
 * 계약 SoT 는 `query-failure-state-policy` 스펙. 갈래 판정은 `queryRenderState` 가 담당한다.
 */

export class EnvelopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EnvelopeError";
  }
}

interface Envelope<T> {
  status?: string;
  message?: string;
  data?: T;
}

/**
 * 봉투가 `status: "Success"` 가 아니거나 아예 도착하지 않았으면 `EnvelopeError` 를 던지고,
 * 성공이면 `data` 를 반환한다.
 *
 * `data` 가 없는 성공 응답(204 등)은 `undefined` 를 그대로 반환한다 — 그 경우의 의미
 * (0건인지, 본문 없는 성공인지)는 호출부가 정한다.
 */
export function unwrapEnvelope<T>(body: Envelope<T> | undefined | null): T | undefined {
  if (!body) {
    throw new EnvelopeError("empty response envelope");
  }
  if (body.status !== "Success") {
    throw new EnvelopeError(
      body.message || `unexpected envelope status: ${String(body.status)}`
    );
  }
  return body.data;
}

/**
 * 목록 응답 전용. 승격까지는 `unwrapEnvelope` 와 같고, 본문 없는 성공을 빈 배열로 정규화한다.
 * "성공했는데 0건"과 "못 가져옴"은 여기서 이미 갈라져 있다 — 후자는 위에서 throw 되었다.
 */
export function unwrapEnvelopeList<T>(body: Envelope<T[]> | undefined | null): T[] {
  return unwrapEnvelope(body) ?? [];
}

/**
 * `data` 가 반드시 있어야 하는 응답 전용. `unwrapEnvelopeList` 의 형제로, 본문 없는 성공을
 * 빈 값으로 접는 대신 오류로 승격한다 — 단일 객체 조회에서 `undefined` 를 그대로 흘리면
 * 소비처가 `response.x` 를 읽다 TypeError 로 크래시하거나 그 부재를 "0건"으로 오독한다.
 *
 * `resourceName` 은 오류 문구에 그대로 들어간다(예: `"drawing-meshes"` →
 * "drawing-meshes 응답 본문에 data 가 없습니다") — 어느 조회가 비었는지 로그에서 갈리게 한다.
 */
export function unwrapEnvelopeRequired<T>(
  body: Envelope<T> | undefined | null,
  resourceName: string
): T {
  const data = unwrapEnvelope(body);
  if (data === undefined || data === null) {
    throw new EnvelopeError(`${resourceName} 응답 본문에 data 가 없습니다`);
  }
  return data;
}
